<template>
  <div class="m3p-oneclick">
    <h3 class="m3p-oneclick__heading">
      <span class="m3p-oneclick__kantan">簡単</span
      >ワンクリックでお問い合わせ<span class="m3p-oneclick__heading_small"
        >この求人にワンクリックでお問い合わせいただけます。</span
      >
    </h3>
    <h4 class="m3p-oneclick__heading2">
      <i class="atlas-glyphs-pencil"></i>お問い合わせ先の{{
        company.name
      }}へメッセージをご入力ください
    </h4>
    <div class="m3p-oneclick__inner">
      <div class="m3p-oneclick__message">
        <message-form
          :message="formData.message"
          @change="(name, value) => updateFormDataValue(name, value)"
        />
      </div>
      <div class="m3p-oneclick__btn">
        <a
          id="detail_contact_top_oneclick"
          @click.prevent="handleOpeningModal"
          class="m3-button m3-button--secondary m3p-oneclick__submit"
          >{{ contactBtnText }}</a
        >
        <p class="m3p-oneclick__change">
          <span class="m3p_bold">{{ fullName }}</span
          >先生ご本人でない場合は<a :href="logout_url">こちら</a>
        </p>
      </div>
    </div>
    <p v-if="!company.is_direct_application_plan" class="m3p-oneclick__text">
      {{
        company.is_m3c
          ? `※これまでにお問い合わせいただいた際の入力情報(お名前・ふりがな・メールアドレス・現住所・電話番号・性別・生まれた年・転職希望時期・就業状況・経験年数)を、コンサルタントの所属するエムスリーキャリア株式会社に提供いたします。`
          : `※これまでにお問い合わせいただいた際の入力情報(お名前・ふりがな・メールアドレス・現住所・電話番号・性別・生まれた年・転職希望時期・就業状況・経験年数)を、コンサルタントの所属する${company.name}及び、エムスリーキャリア株式会社に提供いたします。`
      }}
    </p>

    <modal
      header="お問い合わせ内容をご確認ください"
      :opened="modalOpened"
      @close="handleClosingModal"
      @after-enter="displayRecaptcha"
    >
      <div>
        <p
          v-if="!company.is_direct_application_plan"
          class="m3p-modal__oneclick_text"
        >
          {{
            company.is_m3c
              ? `ご入力いただいたメッセージとこれまでにお問い合わせいただいた際の入力情報を、エムスリーキャリア株式会社に提供いたします。`
              : `ご入力いただいたメッセージとこれまでにお問い合わせいただいた際の入力情報を、${company.name}及び、エムスリーキャリア株式会社に提供いたします。`
          }}
        </p>
        <confirmation :masterData="master_data" :formData="formData" />
        <div class="m3p-modal__oneclick_footer">
          <div
            v-show="should_recaptcha"
            id="google_recaptcha"
            class="g-recaptcha"
            :data-sitekey="site_key"
          ></div>
          <p>
            ※
            <a href="/help/content#06" target="_blank">
              利用規約<i class="atlas-glyphs-external-link"></i></a
            >および、<a href="https://www.m3career.com/privacy/" target="_blank"
              >個人情報保護方針<i class="atlas-glyphs-external-link"></i></a
            >にご同意の上、お問い合わせください。
          </p>
          <div class="m3p-modal__oneclick_btn">
            <button
              @click="fixInquiryFormFromConfirmationModal"
              aria-hidden="true"
              data-dismiss="modal"
              id="fix-inquiry-form"
              class="m3-button m3-button--default"
              type="button"
            >
              修正する
            </button>
            <button
              @click="submitFormData"
              class="m3-button m3-button--secondary"
              type="submit"
              id="submit-oneclick-inquiry"
            >
              送信する<i class="atlas-glyphs-m-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>
<script setup>
import Cookies from 'js-cookie';
import merge from 'lodash/merge';
import sendPositionInquiryMail from '@component_path/lib/call_api/inquiry_mail/positionInquiryMail';
import pcrContents from '@component_path/pages/pc/positions/show/module/pcr_contents';

import Confirmation from './components/Confirmation';
import MessageForm from './components/Message';
import Modal from '@component_path/pages/pc/positions/show/common_components/modal/Modal';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';

const store = useStore();
const { validate } = useForm();

const props = defineProps({
  master_data: {
    type: Object,
    required: true
  },
  form_options: {
    type: Object,
    required: true
  },
  initial_data: {
    type: Object,
    required: true
  },
  logout_url: {
    type: String,
    required: true
  },
  should_recaptcha: {
    type: Boolean,
    required: false,
    default: true
  },
  site_key: {
    type: String,
    required: true
  },
  without_reduced_params_flag: {
    type: Boolean,
    required: false,
    default: false
  },
  company: {
    type: Object,
    required: true
  }
});

const gRecaptcha = ref('');
const formData = ref({});
const grecaptchaResponse = ref(null);

const pcrParams = computed(() => store.state.requestParam.pcrParams);
const isProcessing = computed(() => store.state.requestParam.isProcessing);
const currentAuthenticityToken = computed(
  () => store.state.requestParam.currentAuthenticityToken
);
const modalName = computed(() => store.state.oneClickInquiry.modalName);
const submitFormUrlWithQuery = computed(
  () => store.getters['requestParam/submitFormUrlWithQuery']
);
const modalOpened = computed(() => modalName.value !== '');
const fullName = computed(() => formData.value.name);
const contactBtnText = computed(() =>
  props.company.is_direct_application_plan
    ? 'この求人に直接応募する'
    : 'この求人へのお問い合わせ'
);

const initFormData = formData =>
  store.dispatch('positionInquiryMail/initFormData', formData);
const setIsProcessing = isProcessing =>
  store.dispatch('requestParam/setIsProcessing', isProcessing);
const setSubmitFormUrl = url =>
  store.dispatch('requestParam/setSubmitFormUrl', url);
const setPcrParams = params =>
  store.dispatch('requestParam/setPcrParams', params);
const setCurrentAuthenticityToken = token =>
  store.dispatch('requestParam/setCurrentAuthenticityToken', token);

const setModalName = name =>
  store.dispatch('oneClickInquiry/setModalName', name);
const updateFormDataValue = (field, value) => {
  formData.value[field] = value;
};
const handleOpeningModal = async () => {
  const { valid } = await validate();
  if (!valid) {
    window.scrollTo({ top: 0 });
    setModalName('');
    return;
  }
  setPcrParams({
    key: 'pcr_content',
    value: pcrContents.top_oneclick
  });
  setModalName('oneClickInquiry');
};
const handleClosingModal = () => {
  setPcrParams({ key: 'pcr_content', value: pcrContents.direct });
  setModalName('');
};
const fixInquiryFormFromConfirmationModal = () => {
  handleClosingModal();
  const targetAnchor = document.querySelector('#inquiry-form');
  window.scroll({
    top: targetAnchor.offsetTop,
    behavior: 'smooth'
  });
};
const displayRecaptcha = () => {
  gRecaptcha.value = grecaptcha.render('google_recaptcha');
};

const submitFormData = async () => {
  // 二重送信防止用のフラグが立っていたら、送信しない。
  // TODO
  const { valid } = await validate();
  if (isProcessing.value || !valid) return;
  setIsProcessing(true);

  Object.keys(pcrParams.value).map(key => {
    Cookies.set(key, pcrParams.value[key], { domain: '.m3.com' });
  });

  // recaptchaを読み込んでないページでgrecaptcha.getResponse()がエラーを吐くため
  try {
    if (typeof grecaptcha === 'object') {
      grecaptchaResponse.value = grecaptcha.getResponse();
    }
  } catch (e) {
    // recaptchaを読み込んでないページでgrecaptcha.getResponse()がエラーを吐いて問い合わせ処理が
    // 続行できなくなるため例外処理を書いて問い合わせを続行させる
    // TODO: No reCAPTCHA client existsというエラーを解消する
    grecaptchaResponse.value = null;
  }

  const response = await sendPositionInquiryMail(
    formData.value,
    currentAuthenticityToken.value,
    submitFormUrlWithQuery.value,
    grecaptchaResponse.value
  );
  if (response.status === 200) {
    const redirectUrl = response.data.thanks_page_url;
    location.href = redirectUrl;
    return;
  } else if (response.status === 400) {
    setCurrentAuthenticityToken(response.data.authenticity_token);
  }
  setIsProcessing(false);
};

setSubmitFormUrl(props.form_options.form_url);
setCurrentAuthenticityToken(props.form_options.authenticity_token);
// initFormData()でstoreのformDataを初期化。
initFormData(props.initial_data);
// storeのformDataでこのコンポーネントのformDataを初期化。
// initFormData内で整形しているため、直接initial_dataで初期化はできない
// sexのみ、値がundefinedだとサーバー側で入力値が補完されてしまうので空白にしておく
formData.value = merge({}, store.state.positionInquiryMail.formData, {
  without_reduced_params_flag: props.without_reduced_params_flag,
  sex: store.state.positionInquiryMail.formData.sex || ''
});
</script>
